import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

function ChangePasswordRow({ user }: { user: User | null }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const isOAuthOnly = user?.app_metadata?.provider !== 'email';

  if (isOAuthOnly) {
    return (
      <div className="sfield">
        <div className="sfield-row">
          <div className="sfield-label">
            <span>Password</span>
            <span className="sfield-hint">You sign in with Google — password is managed by your Google account.</span>
          </div>
        </div>
      </div>
    );
  }

  async function handleSendReset() {
    if (!user?.email) return;
    setStatus('loading');
    setErrorMsg('');
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}?reset=1`,
    });
    if (error) {
      setErrorMsg(error.message);
      setStatus('error');
    } else {
      setStatus('sent');
    }
  }

  return (
    <div className="sfield">
      <div className="sfield-row">
        <div className="sfield-label">
          <span>Password</span>
          {status === 'sent' && (
            <span className="sfield-hint" style={{ color: 'var(--fg-2)' }}>
              Reset link sent to {user?.email} — check your inbox.
            </span>
          )}
          {status === 'error' && (
            <span className="sfield-hint" style={{ color: 'var(--danger)' }}>{errorMsg}</span>
          )}
        </div>
        {status === 'sent' ? (
          <button className="sfield-edit-btn" onClick={() => setStatus('idle')}>Resend</button>
        ) : (
          <button
            className="sfield-edit-btn"
            onClick={handleSendReset}
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Sending…' : 'Change'}
          </button>
        )}
      </div>
    </div>
  );
}

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$0',
    period: 'forever',
    features: ['10 analyses / month', 'All 3 draft styles', 'Inline suggestions — up to 25/day', 'Dashboard analytics', 'All platforms'],
    recommended: false,
    isTeam: false,
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '$29',
    period: '/ mo',
    features: ['150 analyses / month', 'All 3 draft styles', 'Inline suggestions — up to 75/day', 'Unlimited auto-learning knowledge base', 'Draft refinement', 'Dashboard analytics', 'All platforms', 'Standard support'],
    recommended: false,
    isTeam: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$79',
    period: '/ mo',
    features: ['500 analyses / month', 'All 3 draft styles', 'Inline suggestions — up to 200/day', 'Unlimited auto-learning knowledge base', 'Draft refinement', 'Dashboard analytics', 'All platforms', 'Priority support'],
    recommended: true,
    isTeam: false,
  },
  {
    id: 'team',
    name: 'Team',
    price: 'Custom',
    period: '',
    features: ['5,000 analyses / month', 'Everything in Pro', 'Unlimited seats', 'Dedicated onboarding', 'Team reporting (coming soon)', 'Early access to custom playbooks'],
    recommended: false,
    isTeam: true,
  },
];



const INDUSTRY_OPTIONS = [
  { value: 'automotive', label: 'Automotive Dealerships' },
  { value: 'real_estate', label: 'Real Estate & Mortgage' },
  { value: 'saas', label: 'SaaS & Technology' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'healthcare', label: 'Healthcare & Medical' },
  { value: 'financial', label: 'Financial Services' },
];

interface ProfileData {
  full_name: string;
  recovery_email: string;
  industry: string;
  role: string;
  bio: string;
}

interface SettingsPageProps {
  user: User | null;
  activeTab: 'profile' | 'subscription' | 'settings';
}

function EditableRow({
  label,
  hint,
  value,
  type = 'text',
  options,
  onSave,
}: {
  label: string;
  hint?: string;
  value: string;
  type?: 'text' | 'email' | 'textarea' | 'select';
  options?: { value: string; label: string }[];
  onSave: (val: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setDraft(value); }, [value]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setDraft(value);
    setEditing(false);
    setError('');
  }

  const displayValue = type === 'select'
    ? options?.find(o => o.value === value)?.label ?? ''
    : value;

  return (
    <div className="sfield">
      <div className="sfield-row">
        <div className="sfield-label">
          <span>{label}</span>
          {hint && <span className="sfield-hint">{hint}</span>}
        </div>

        {!editing ? (
          <>
            <span className="sfield-value">{displayValue || <em className="sfield-empty">Not set</em>}</span>
            <button className="sfield-edit-btn" onClick={() => setEditing(true)}>Edit</button>
          </>
        ) : (
          <div className="sfield-edit-area">
            {type === 'textarea' ? (
              <textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                rows={3}
                maxLength={400}
                className="sfield-input"
                autoFocus
              />
            ) : type === 'select' ? (
              <select value={draft} onChange={e => setDraft(e.target.value)} className="sfield-input" autoFocus>
                <option value="">Select…</option>
                {options?.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                type={type}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                className="sfield-input"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
              />
            )}
            {error && <span className="sfield-error">{error}</span>}
            <div className="sfield-actions">
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={handleCancel} disabled={saving}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ user, activeTab }) => {
  const [activePlan, setActivePlan] = useState<'starter' | 'basic' | 'pro' | null>(null);
  const [deleteState, setDeleteState] = useState<'idle' | 'confirming' | 'deleting' | 'error'>('idle');
  const [deleteError, setDeleteError] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [profile, setProfile] = useState<ProfileData>({
    full_name: user?.user_metadata?.full_name ?? '',
    recovery_email: '',
    industry: '',
    role: '',
    bio: '',
  });
  const [profileLoading, setProfileLoading] = useState(true);

  // Stripe subscription management state
  const [subDetails, setSubDetails] = useState<{
    subscriptionId?: string;
    status?: string;
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: number;
    priceId?: string;
    interval?: string;
  } | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subActionLoading, setSubActionLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const loadSubscriptionDetails = async () => {
    setSubLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'get-details' },
      });
      if (!error && data && data.hasActiveSubscription) {
        setSubDetails(data);
        if (data.interval === 'year') {
          setBillingPeriod('annual');
        } else {
          setBillingPeriod('monthly');
        }
      } else {
        setSubDetails(null);
      }
    } catch (err) {
      console.error('Failed to load subscription details:', err);
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    async function load() {
      const { data } = await supabase
        .from('profiles')
        .select('full_name, recovery_email, industry, role, bio, subscription_status, price_id')
        .eq('id', user!.id)
        .single();
      if (data) {
        setProfile({
          full_name: data.full_name ?? user?.user_metadata?.full_name ?? '',
          recovery_email: data.recovery_email ?? '',
          industry: data.industry ?? '',
          role: data.role ?? '',
          bio: data.bio ?? '',
        });
        if (data.subscription_status === 'active' || data.subscription_status === 'trialing') {
          const priceId = data.price_id;
          if (priceId && (priceId === 'price_1TaKQmPWi47nn0IbWT6zlFdI' || priceId === 'price_1TaKQrPWi47nn0Ibf8YHTzTJ')) {
            setActivePlan('basic');
          } else {
            setActivePlan('pro');
          }
          void loadSubscriptionDetails();
        } else {
          setActivePlan('starter');
          setSubDetails(null);
        }
      }
      setProfileLoading(false);
    }
    load();
  }, [user]);

  async function saveField(field: keyof ProfileData, value: string) {
    if (!user) throw new Error('Not signed in');
    const { error } = await supabase.from('profiles').update({ [field]: value }).eq('id', user.id);
    if (error) throw new Error(error.message);
    setProfile(p => ({ ...p, [field]: value }));
    // Tell the extension's auth-sync content script to re-cache the profile so
    // the side panel, popup, and inline suggestions reflect the edit immediately.
    try { window.postMessage({ type: 'salifly-ext-profile-updated' }, window.location.origin); } catch { /* ignore */ }
  }

  async function handleDeleteAccount() {
    setDeleteState('deleting');
    setDeleteError('');
    const { error } = await supabase.rpc('delete_user');
    if (error) {
      setDeleteError(error.message);
      setDeleteState('error');
      return;
    }
    await supabase.auth.signOut();
  }

  const handleCancelSubscription = async () => {
    setSubActionLoading(true);
    setUpgradeError('');
    try {
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'cancel' },
      });
      if (error) throw new Error(error.message);
      if (data && data.success) {
        await loadSubscriptionDetails();
        setActivePlan('starter');
        setShowCancelModal(false);
        try { window.postMessage({ type: 'salifly-ext-profile-updated' }, window.location.origin); } catch { /* ignore */ }
      }
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : 'Could not cancel subscription. Please try again.');
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setSubActionLoading(true);
    setUpgradeError('');
    try {
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'reactivate' },
      });
      if (error) throw new Error(error.message);
      if (data && data.success) {
        await loadSubscriptionDetails();
        const { data: profileData } = await supabase
          .from('profiles')
          .select('subscription_status, price_id')
          .eq('id', user!.id)
          .single();
        if (profileData) {
          const priceId = profileData.price_id;
          if (priceId && (priceId === 'price_1TaKQmPWi47nn0IbWT6zlFdI' || priceId === 'price_1TaKQrPWi47nn0Ibf8YHTzTJ')) {
            setActivePlan('basic');
          } else {
            setActivePlan('pro');
          }
        }
        try { window.postMessage({ type: 'salifly-ext-profile-updated' }, window.location.origin); } catch { /* ignore */ }
      }
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : 'Could not reactivate subscription. Please try again.');
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleChangePlan = async (planId: 'basic' | 'pro', billing: 'monthly' | 'annual') => {
    setSubActionLoading(true);
    setUpgradeError('');
    try {
      const planName = planId === 'basic' ? 'Basic' : 'Pro';
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'change-plan', plan: planName, billing },
      });
      if (error) throw new Error(error.message);
      if (data && data.success) {
        await loadSubscriptionDetails();
        setActivePlan(planId);
        try { window.postMessage({ type: 'salifly-ext-profile-updated' }, window.location.origin); } catch { /* ignore */ }
      }
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : 'Could not change plan. Please try again.');
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleUpgrade = async (planId: string, isTeam: boolean) => {
    if (isTeam) { window.location.href = 'mailto:sales@salifly.com'; return; }
    const plan = planId === 'basic' ? 'Basic' : planId === 'pro' ? 'Pro' : null;
    if (!plan) return;

    if (activePlan && activePlan !== 'starter') {
      await handleChangePlan(planId as 'basic' | 'pro', billingPeriod);
      return;
    }

    setUpgradeError('');
    setUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { plan, billing: billingPeriod },
      });
      if (error || !data?.url) {
        throw new Error('Could not start checkout. Please try again.');
      }
      window.location.href = data.url;
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
      setUpgrading(false);
    }
  };

  return (
    <main className="main-content" style={{ padding: 0 }}>
      <div className="settings-content-area" style={{ maxWidth: activeTab === 'subscription' ? '920px' : '680px' }}>

        {/* ── Profile ── */}
        {activeTab === 'profile' && (
          <div className="settings-tab-pane fade-in">
            <div className="settings-hero">
              <h1 className="settings-page-title">Profile</h1>
              <p className="settings-page-sub">Manage your personal information and sales context.</p>
            </div>

            {profileLoading ? (
              <p style={{ color: 'var(--fg-3)', fontSize: 13 }}>Loading…</p>
            ) : (
              <>
                <section className="sgroup">
                  <h3 className="sgroup-label">Account</h3>
                  <div className="sgroup-body">
                    <EditableRow label="Full name" value={profile.full_name} onSave={v => saveField('full_name', v)} />
                    <div className="sfield">
                      <div className="sfield-row">
                        <span className="sfield-label">Email</span>
                        <span className="sfield-value">{user?.email}</span>
                      </div>
                    </div>
                    <EditableRow label="Recovery email" hint="Backup for account recovery" value={profile.recovery_email} type="email" onSave={v => saveField('recovery_email', v)} />
                    <ChangePasswordRow user={user} />
                  </div>
                </section>

                <section className="sgroup">
                  <h3 className="sgroup-label">Sales profile</h3>
                  <p className="sgroup-desc">Salifly uses this to personalize AI drafts and playbooks to your context.</p>
                  <div className="sgroup-body">
                    <EditableRow label="Industry" value={profile.industry} type="select" options={INDUSTRY_OPTIONS} onSave={v => saveField('industry', v)} />
                    <EditableRow label="Role" hint="e.g. Account Executive, SDR" value={profile.role} onSave={v => saveField('role', v)} />
                    <EditableRow label="About you" hint="Short bio to personalize your messaging" value={profile.bio} type="textarea" onSave={v => saveField('bio', v)} />
                  </div>
                </section>

                <section className="sgroup sgroup--danger">
                  <h3 className="sgroup-label" style={{ color: 'var(--danger)' }}>Danger zone</h3>
                  <div className="sgroup-body">
                    <div className="sfield">
                      <div className="sfield-row" style={{ flexWrap: 'wrap', gap: 12 }}>
                        <div className="sfield-label">
                          <span>Delete account</span>
                          <span className="sfield-hint">Permanently removes your account and all data. This cannot be undone.</span>
                        </div>
                        {deleteState === 'idle' && (
                          <button
                            className="btn btn-sec btn-sm"
                            style={{ color: 'var(--danger)', borderColor: 'var(--danger)', flexShrink: 0 }}
                            onClick={() => setDeleteState('confirming')}
                          >
                            Delete
                          </button>
                        )}
                        {deleteState === 'confirming' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                            <p style={{ font: '400 13px/1.5 var(--font-sans)', color: 'var(--danger)', margin: 0 }}>
                              This will permanently delete your account, all leads, and all draft history. Are you sure?
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="btn btn-sm"
                                style={{ background: 'var(--danger)', color: '#fff', border: 'none' }}
                                onClick={handleDeleteAccount}
                              >
                                Yes, delete my account
                              </button>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setDeleteState('idle')}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        {deleteState === 'deleting' && (
                          <span style={{ font: '400 13px var(--font-sans)', color: 'var(--fg-3)', flexShrink: 0 }}>Deleting…</span>
                        )}
                        {deleteState === 'error' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                            <p style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--danger)', margin: 0 }}>{deleteError}</p>
                            <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setDeleteState('idle')}>
                              Try again
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        )}

        {/* ── Subscription ── */}
        {activeTab === 'subscription' && (() => {
          const gridPlans = PLANS.filter(p => p.id !== 'team');
          const teamPlan = PLANS.find(p => p.id === 'team');

          return (
            <div className="settings-tab-pane fade-in">
              <div className="settings-hero" style={{ marginBottom: 32 }}>
                <h1 className="settings-page-title">Subscription</h1>
                <p className="settings-page-sub">Manage your plan billing and subscription preferences.</p>
              </div>

              {/* Active Plan Overview */}
              {activePlan && activePlan !== 'starter' && (
                <section className="sgroup" style={{ marginBottom: 32 }}>
                  <h3 className="sgroup-label">Active Plan</h3>
                  <div className="sgroup-body" style={{
                    padding: '24px',
                    background: 'var(--bg-surface-alt)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid var(--border-2)',
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
                    borderRadius: '12px'
                  }}>
                    {(() => {
                      const isCanceling = subDetails?.cancelAtPeriodEnd ?? false;
                      const renewalDate = subDetails?.currentPeriodEnd ? formatDate(subDetails.currentPeriodEnd) : 'next renewal';
                      const intervalText = subDetails?.interval === 'year' || billingPeriod === 'annual' ? 'Annual' : 'Monthly';

                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
                          <div>
                            <h4 style={{ font: '600 18px/1.2 var(--font-sans)', color: 'var(--fg-1)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                              {activePlan === 'pro' ? 'Pro Plan' : 'Basic Plan'}
                              {isCanceling && (
                                <span style={{
                                  background: 'rgba(220, 38, 38, 0.1)',
                                  color: '#dc2626',
                                  borderColor: 'rgba(220, 38, 38, 0.2)',
                                  fontSize: 10,
                                  textTransform: 'uppercase',
                                  fontWeight: 700,
                                  padding: '2px 8px',
                                  borderRadius: 100,
                                  border: '1px solid',
                                  letterSpacing: '0.04em'
                                }}>
                                  Canceling
                                </span>
                              )}
                            </h4>
                            <div style={{ font: '500 13px/1.4 var(--font-sans)', color: 'var(--fg-2)', marginTop: '8px' }}>
                              {subLoading ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--fg-3)' }}>
                                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '1.5px solid var(--border-1)', borderTopColor: 'var(--accent)', animation: 'spin 0.7s linear infinite' }} />
                                  Loading billing schedule…
                                </span>
                              ) : (
                                <>
                                  Billing cycle: <span style={{ color: 'var(--fg-1)' }}>{intervalText}</span>
                                  {isCanceling && (
                                    <>
                                      <span style={{ color: 'var(--fg-4)', margin: '0 8px' }}>•</span>
                                      Ends: <span style={{ color: '#dc2626', fontWeight: 600 }}>{renewalDate}</span> <span style={{ color: 'var(--fg-3)' }}>(Downgrades to Starter)</span>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: 8 }}>
                            {isCanceling ? (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={handleReactivateSubscription}
                                disabled={subActionLoading}
                                style={{ borderRadius: '100px', padding: '6px 16px', fontWeight: 600 }}
                              >
                                {subActionLoading ? 'Reactivating…' : 'Reactivate Subscription'}
                              </button>
                            ) : (
                              <button
                                className="btn btn-sec btn-sm"
                                style={{ color: 'var(--danger)', borderColor: 'var(--border-1)', borderRadius: '100px', padding: '6px 16px', fontWeight: 600 }}
                                onClick={() => setShowCancelModal(true)}
                                disabled={subActionLoading || subLoading}
                              >
                                Cancel Subscription
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </section>
              )}

              {/* Plans List Header & Toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 className="sgroup-label" style={{ margin: 0, fontSize: '11px', letterSpacing: '0.06em' }}>
                  {activePlan && activePlan !== 'starter' ? 'Switch Plans' : 'Available Plans'}
                </h3>
                <div style={{ display: 'inline-flex', background: 'var(--bg-sunken)', padding: 3, borderRadius: '100px', border: '1px solid var(--border-2)' }}>
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    disabled={subLoading || subActionLoading}
                    style={{
                      border: 'none',
                      background: billingPeriod === 'monthly' ? 'var(--bg-surface)' : 'transparent',
                      color: billingPeriod === 'monthly' ? 'var(--fg-1)' : 'var(--fg-3)',
                      padding: '4px 14px',
                      borderRadius: '100px',
                      font: '600 11px var(--font-sans)',
                      cursor: 'pointer',
                      boxShadow: billingPeriod === 'monthly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    disabled={subLoading || subActionLoading}
                    style={{
                      border: 'none',
                      background: billingPeriod === 'annual' ? 'var(--bg-surface)' : 'transparent',
                      color: billingPeriod === 'annual' ? 'var(--fg-1)' : 'var(--fg-3)',
                      padding: '4px 14px',
                      borderRadius: '100px',
                      font: '600 11px var(--font-sans)',
                      cursor: 'pointer',
                      boxShadow: billingPeriod === 'annual' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Annually (Save 20%)
                  </button>
                </div>
              </div>

              {/* Plan List in Apple-Style Horizontal Tiers Layout */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '32px'
              }}>
                {gridPlans.map(plan => {
                  const isCurrent = activePlan === plan.id;
                  const planRank: Record<string, number> = { starter: 0, basic: 1, pro: 2, team: 3 };
                  const currentRank = activePlan ? (planRank[activePlan] ?? 0) : 0;
                  const targetRank = planRank[plan.id] ?? 0;
                  const isDowngrade = currentRank > targetRank;

                  // Price mapping
                  let price = plan.price;
                  let period = plan.period;
                  if (plan.id === 'basic') {
                    price = billingPeriod === 'monthly' ? '$29' : '$23';
                    period = '/ mo';
                  } else if (plan.id === 'pro') {
                    price = billingPeriod === 'monthly' ? '$79' : '$63';
                    period = '/ mo';
                  }

                  // CTA Button logic
                  let btnText = 'Upgrade';
                  let btnClass = plan.recommended && !isCurrent ? 'btn-primary' : 'btn-sec';
                  let btnDisabled = upgrading || subLoading || subActionLoading;
                  let onClickAction = () => handleUpgrade(plan.id, plan.isTeam);

                  if (isCurrent) {
                    btnText = 'Current plan';
                    btnClass = 'btn-ghost';
                    btnDisabled = true;
                  } else if (plan.id === 'starter') {
                    btnText = 'Downgrade';
                    btnClass = 'btn-sec';
                    btnDisabled = true; // Handled by cancelling the active subscription above
                  } else if (activePlan && activePlan !== 'starter') {
                    btnText = isDowngrade ? 'Downgrade' : 'Upgrade';
                    btnClass = 'btn-sec';
                    onClickAction = () => handleChangePlan(plan.id as 'basic' | 'pro', billingPeriod);
                  }

                  const isRecommended = plan.recommended && !isCurrent;

                  return (
                    <div
                      key={plan.id}
                      style={{
                        background: isCurrent ? 'var(--accent-wash)' : 'var(--bg-surface)',
                        border: isCurrent
                          ? '1px solid var(--accent)'
                          : isRecommended
                            ? '1px solid var(--accent-hover)'
                            : '1px solid var(--border-1)',
                        borderRadius: '16px',
                        padding: '24px 32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '24px',
                        flexWrap: 'wrap',
                        transition: 'all 0.2s ease',
                        boxShadow: isRecommended
                          ? '0 6px 20px rgba(94, 92, 230, 0.04)'
                          : 'none',
                      }}
                    >
                      {/* Left side: Plan title & Price */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', minWidth: '180px', flex: '1 1 auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ font: '600 15px/1 var(--font-sans)', color: 'var(--fg-1)', letterSpacing: '-0.01em' }}>
                              {plan.name}
                            </span>
                            {isCurrent && (
                              <span style={{
                                font: '600 9px/1 var(--font-sans)',
                                color: '#059669',
                                background: 'rgba(5, 150, 105, 0.1)',
                                borderRadius: '100px',
                                padding: '3px 8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                              }}>
                                Current
                              </span>
                            )}
                            {plan.recommended && !isCurrent && (
                              <span style={{
                                font: '600 9px/1 var(--font-sans)',
                                color: '#5e5ce6',
                                background: 'rgba(94, 92, 230, 0.1)',
                                borderRadius: '100px',
                                padding: '3px 8px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                              }}>
                                Popular
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
                            <span style={{ font: '600 28px/1 var(--font-serif)', color: 'var(--fg-1)', fontFamily: 'var(--font-serif)' }}>{price}</span>
                            <span style={{ font: '400 12px/1 var(--font-sans)', color: 'var(--fg-3)' }}>{period}</span>
                          </div>
                          {billingPeriod === 'annual' && plan.id !== 'starter' && (
                            <span style={{ font: '400 10px var(--font-sans)', color: 'var(--fg-4)' }}>
                              Billed annually (${plan.id === 'basic' ? 23 * 12 : 63 * 12}/yr)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Middle: Features checklist */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '10px 24px',
                        flex: '2 1 300px',
                        maxWidth: '460px'
                      }}>
                        {plan.features.map(f => (
                          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', font: '400 12px/1.4 var(--font-sans)', color: 'var(--fg-2)' }}>
                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden style={{ color: 'var(--accent)', flexShrink: 0 }}>
                              <path d="M2 7l3.5 3.5L12 3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>

                      {/* Right: Button */}
                      <div style={{ display: 'flex', alignItems: 'center', minWidth: '120px', justifyContent: 'flex-end', flex: '1 1 auto' }}>
                        <button
                          className={`btn btn-sm ${btnClass}`}
                          disabled={btnDisabled}
                          onClick={onClickAction}
                          style={{
                            borderRadius: '100px',
                            padding: '8px 24px',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: '34px',
                            width: '100%',
                            maxWidth: '140px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {btnText}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Enterprise/Team Banner */}
              {teamPlan && (
                <div style={{
                  background: 'linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-surface-alt) 100%)',
                  border: '1px solid var(--border-1)',
                  borderRadius: '16px',
                  padding: '24px 32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '24px',
                  flexWrap: 'wrap',
                  marginTop: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.01)'
                }}>
                  <div style={{ flex: '1 1 300px' }}>
                    <h4 style={{ font: '600 15px/1.2 var(--font-sans)', color: 'var(--fg-1)', margin: '0 0 4px' }}>
                      {teamPlan.name} Plan
                    </h4>
                    <p style={{ font: '400 12px/1.5 var(--font-sans)', color: 'var(--fg-3)', margin: 0 }}>
                      {teamPlan.features.join(' • ')}
                    </p>
                  </div>
                  <button
                    className="btn btn-sm btn-sec"
                    onClick={() => { window.location.href = 'mailto:sales@salifly.com'; }}
                    style={{
                      borderRadius: '100px',
                      padding: '8px 20px',
                      fontWeight: 600,
                      fontSize: '12px',
                      height: '34px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Contact sales
                  </button>
                </div>
              )}

              {upgradeError && (
                <p style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--danger)', marginTop: 16 }}>
                  {upgradeError}
                </p>
              )}

              {/* Cancel Modal */}
              {showCancelModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(9, 9, 11, 0.8)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: 20
                }}>
                  <div style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-1)',
                    borderRadius: 16,
                    maxWidth: 420,
                    width: '100%',
                    padding: '24px 24px',
                    boxShadow: 'var(--shadow-pop)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16
                  }}>
                    <h2 style={{ font: '600 18px/1.2 var(--font-sans)', color: 'var(--fg-1)', margin: 0 }}>
                      Cancel Subscription
                    </h2>
                    <p style={{ font: '400 13px/1.5 var(--font-sans)', color: 'var(--fg-3)', margin: 0 }}>
                      Are you sure you want to cancel your paid plan? You will lose access to premium features (like draft refinement, the auto-learning knowledge base, and higher usage limits) at the end of your billing cycle.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowCancelModal(false)}
                        disabled={subActionLoading}
                      >
                        Keep plan
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }}
                        onClick={handleCancelSubscription}
                        disabled={subActionLoading}
                      >
                        {subActionLoading ? 'Canceling…' : 'Cancel Subscription'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activePlan && activePlan !== 'starter' && (
                <p style={{ font: '400 12px/1.4 var(--font-sans)', color: 'var(--fg-4)', marginTop: 24 }}>
                  Need to downgrade to Starter? Click "Cancel Subscription" at the top. Downgrades or cancellations take effect at the end of the billing cycle.
                </p>
              )}
            </div>
          );
        })()}

        {/* ── Settings ── */}
        {activeTab === 'settings' && (
          <div className="settings-tab-pane fade-in">
            <div className="settings-hero">
              <h1 className="settings-page-title">Settings</h1>
              <p className="settings-page-sub">Manage your app preferences.</p>
            </div>
            <section className="sgroup">
              <h3 className="sgroup-label">Appearance</h3>
              <div className="sgroup-body">
                <div className="sfield">
                  <div className="sfield-row">
                    <span className="sfield-label">Theme</span>
                    <span className="sfield-value">Light</span>
                    <button className="sfield-edit-btn">Change</button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

      </div>
    </main>
  );
};
